import RiCheckboxCircleFill from '@ben/icons/checkbox-circle-fill';
import RiErrorWarningFill from '@ben/icons/error-warning-fill';
import RiInformationFill from '@ben/icons/information-fill';
import RIcon from '@ben/orion/remix-icon';
import Typography, { TypographyVariant } from '@ben/orion/typography';
import type { DefaultTheme, StyledComponent } from 'styled-components';
import styled, { css, useTheme } from 'styled-components';
import { variant } from 'styled-system';

const VARIANT_ICON = {
  error: RiErrorWarningFill,
  warning: RiInformationFill,
  success: RiCheckboxCircleFill,
  info: RiInformationFill,
};

type InputFeedbackVariant = keyof typeof VARIANT_ICON;

interface InputFeedbackProps {
  infoMessage?: string;
  errorMessage?: string;
  warningMessage?: string;
  successMessage?: string;
  id?: string;
  className?: string;
  showInfoIcon?: boolean;
}

export const InputFeedback = ({
  errorMessage,
  infoMessage,
  warningMessage,
  successMessage,
  id,
  className,
  showInfoIcon = false,
}: InputFeedbackProps) => {
  const theme = useTheme();

  if (!infoMessage && !errorMessage && !warningMessage && !successMessage) {
    return;
  }

  return (
    <Wrapper id={id} className={className}>
      {infoMessage && (
        <Message>
          {showInfoIcon && (
            <RIcon icon={VARIANT_ICON.info} color={theme.palette.neutral[90]} />
          )}
          <Typography
            variant={TypographyVariant.bodySm1}
            color={theme.palette.neutral[90]}
          >
            {infoMessage}
          </Typography>
        </Message>
      )}
      {errorMessage && (
        <Message variant="error">
          <RIcon icon={VARIANT_ICON.error} />
          <Typography variant={TypographyVariant.bodySm2} color="inherit">
            {errorMessage}
          </Typography>
        </Message>
      )}
      {warningMessage && (
        <Message variant="warning">
          <RIcon icon={VARIANT_ICON.warning} />
          <Typography variant={TypographyVariant.bodySm2} color="inherit">
            {warningMessage}
          </Typography>
        </Message>
      )}
      {successMessage && (
        <Message variant="success">
          <RIcon icon={VARIANT_ICON.success} />
          <Typography variant={TypographyVariant.bodySm2} color="inherit">
            {successMessage}
          </Typography>
        </Message>
      )}
    </Wrapper>
  );
};

export default InputFeedback;

const Wrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(1)};
    margin-top: ${theme.spacing(1)};
  `}
`;

const baseStyle = ({ theme }: { theme: DefaultTheme }) => css`
  display: flex;
  align-items: center;
  gap: ${theme.spacing(0.5)};
`;

const variantStyle = ({ theme }: { theme: DefaultTheme }) =>
  variant({
    variants: {
      error: {
        color: theme.palette.error.main,
      },
      warning: {
        color: theme.palette.warning.main,
      },
      success: {
        color: theme.palette.success.dark,
      },
    },
  });

const Message: StyledComponent<
  'div',
  DefaultTheme,
  { variant?: InputFeedbackVariant }
> = styled('div')(baseStyle, variantStyle);
